/**
 * Player death (spec §9.2; plan §6 M2-06): the whole hold goes into the death wreck, the ship is rebuilt at
 * `lastDockedStation` with a full hull and restocked torpedoes (fitted modules are kept), and the rebuild fee is
 * charged: `rebuild.base + moduleValueShare × fitted module value`, halved (× `insuredFeeMult`) when the ship is
 * owned and insured. Before ownership the fee is added to the principal; after ownership it comes out of the
 * wallet and any shortfall becomes interest-free `debt`. A repo crew active at the time adds `repoFee` the same way.
 * Goods (the `cargo.parts` block, relay parts included; economy plan SY-2b, U1, D-029 "the cargo stays in the
 * wreck") go into the pod as `contents.goods` with the rest of the hold, and recovering the pod returns them
 * (cargo.ts `addComponent`). Before SY-2b they were destroyed (M3 D-TBD 40).
 * M7 (spec §2.8, SL-2): story items go into the pod with the rest of the hold (their record gets the pod id,
 * `death:<n>`), and the outcome marks the pod `story`, so the salvage loot check never empties them.
 * M6 (plan P21, M6 spec §4): insurance is per ship. The flown ship's insurance reads its hangar entry
 * (`fleet-link/insurance.ts flownShipInsured`); the lease ship keeps `lease.insured`.
 * M6 (plan P24b, D-394 gap 2): an uninsured flown fleet ship is a total loss (`fleet-link/loss.ts`): no rebuild fee,
 * its hangar entry goes and the pilot is back in the parked lease ship (`lostShip` names it for the worker).
 *
 * Pure: the input career is never changed. Fees are whole credits.
 */
import type { M2Data } from '../data/m2Schemas'
import type { ShipDef } from '../data/schemas'
import { flownShipInsured } from '../fleet-link/insurance'
import { flownEntry } from '../fleet-link/hangar'
import { fallBackToLeaseShip, lostCarriedOf, totalLossOf } from '../fleet-link/loss'
import { settleCarried } from '../fleet-link/parkedL'
import type { ShipLimitsOf } from '../fleet-link/switch'
import { contentsHaveStory, deathPodId, markItemsInDeathPod, syncStoryHold } from '../story/items'
import { EPSILON, emptyWares, moduleValue } from './ops'
import { GOOD_IDS, emptyParts, type Goods } from './parts'
import { ok, type CareerEffect, type CmdResult } from './result'
import type { CargoContents, CargoState, Career, LeaseEvent, ParkedL } from './types'

type DeathData = Pick<M2Data, 'lease' | 'modules'>

export interface DeathOptions {
  /** A repo crew was active (spawned and not defeated) when the player died: the repo fee is added. */
  repoActive: boolean
  /** The player's derived ship def (M2-05 `deriveShipDef`): full hull and torpedo rounds come from it. */
  shipDef: Pick<ShipDef, 'hull' | 'torpedoRounds'>
  /**
   * M6 P24b: full hull and torpedo rounds per ship def, for the lease ship an uninsured fleet ship's total loss
   * falls back to. Absent: no total loss (the flown ship is rebuilt with the fee, as before P24b).
   */
  limitsOf?: ShipLimitsOf
  /** M6 P24b: whether the runtime can respawn at a Career station id (the parked lease ship's). Default: never. */
  stationKnown?: (stationId: string) => boolean
  /**
   * M10 P5a11b: the live pose of the L the flown S rides in (`parkedL.ts liveParkedLRecord`). A death on the pad
   * (`Career.carriedBy` still set) leaves that L parked there (`settleCarried`); absent or null: none known.
   */
  carrier?: Readonly<ParkedL> | null
}

export interface DeathOutcome {
  career: Career
  /** The lost cargo, for the death wreck's pod (`SalvageRuntime.spawnDeathWreck`). */
  contents: CargoContents
  /**
   * M7 (spec §2.8): the pod carries story items (it skips the loot roll for them). The salvage runtime reads the
   * same fact from the contents (`contentsHaveStory`), so it survives save and load.
   */
  story: boolean
  /** M7: the pod id the story record now names (`death:<n>`), or null when the pod carries no story item. */
  storyPod: string | null
  /** M6 P24b: the fleet id of an uninsured flown fleet ship lost outright (the worker frees its slot). */
  lostShip?: string
  /** M10 P5a11b: the fleet S that rode in the cradle of `lostShip` (lost with it; the worker frees its slot too). */
  lostCarried?: string
  /**
   * M6 (D-459 bug 5): the player died in a flown fleet ship (not the lease ship), for the death screen's wording:
   * `lost` when it was lost outright (uninsured), and whether it was insured. Absent for a death in the lease ship.
   */
  fleetShip?: { lost: boolean; insured: boolean }
}

/** Credits of the fitted modules (locker modules are not on the ship and do not count). */
export function fittedModuleValue(career: Career, data: Pick<M2Data, 'modules'>): number {
  let total = 0
  for (const id of Object.values(career.ship.fitted)) total += moduleValue(data, id)
  return total
}

/** The rebuild fee for this career right now (without the repo fee). */
export function rebuildFee(career: Career, data: DeathData): number {
  const rebuild = data.lease.rebuild
  let fee = rebuild.base + rebuild.moduleValueShare * fittedModuleValue(career, data)
  // M6 (P21): the flown ship's own insurance (its hangar entry; the lease ship's is `lease.insured`).
  if (flownShipInsured(career)) fee *= data.lease.insurance.insuredFeeMult
  return Math.round(fee)
}

export function applyDeath(input: Career, data: DeathData, opts: DeathOptions): CmdResult<DeathOutcome> {
  // M10 P5a11b: the S died on its L's pad: the L stays parked where it is and the pilot respawns at the station.
  const released = settleCarried(input, opts.carrier ?? null)
  // M7: the hold mirrors are repaired from the story record first, so every story item aboard goes into the pod.
  const career = released.story !== undefined ? syncStoryHold(released) : released
  const t = career.gameTime
  // M6 P24b: an uninsured flown fleet ship is lost outright, so nothing is rebuilt and no rebuild fee is charged.
  const lost = opts.limitsOf !== undefined ? totalLossOf(career) : null
  const fee = lost !== null ? 0 : rebuildFee(career, data)
  const repoFee = opts.repoActive ? Math.round(data.lease.repoFee) : 0
  const total = fee + repoFee
  const effects: CareerEffect[] = []
  const history: LeaseEvent[] = [...career.lease.history]
  const owned = career.lease.owned

  let wallet = career.wallet
  let debt = career.debt
  let principal = career.lease.principal
  if (!owned) {
    principal += total
    if (fee > 0) history.push({ t, kind: 'rebuildFee', amount: fee })
    if (repoFee > 0) history.push({ t, kind: 'repoFee', amount: repoFee })
  } else {
    const paid = Math.min(total, wallet)
    wallet -= paid
    if (fee > 0) history.push({ t, kind: 'rebuildFee', amount: fee })
    if (repoFee > 0) history.push({ t, kind: 'repoFee', amount: repoFee })
    const shortfall = total - paid
    if (shortfall > EPSILON) {
      debt += shortfall
      history.push({ t, kind: 'debtIncurred', amount: shortfall })
      effects.push({ kind: 'debtIncurred', amount: shortfall, debt })
    }
  }
  effects.unshift({ kind: 'deathFee', amount: fee, repoFee, addedToPrincipal: !owned })
  const limit = data.lease.historyLimit
  if (history.length > limit) history.splice(0, history.length - limit)

  const contents: CargoContents = { wares: { ...career.cargo.wares }, components: [...career.cargo.components] }
  const goods = carriedGoods(career.cargo.parts)
  if (goods !== null) contents.goods = goods
  const cargo: CargoState = { ...career.cargo, wares: emptyWares(), components: [] }
  if (cargo.parts !== undefined) cargo.parts = emptyParts()
  const story = contentsHaveStory(contents)
  const storyPod = story ? deathPodId(career.stats.deaths + 1) : null
  const rebuilt: Career = {
    ...(storyPod !== null ? markItemsInDeathPod(career, storyPod) : career),
    wallet: wallet < EPSILON ? 0 : wallet,
    debt,
    lease: { ...career.lease, principal, history },
    cargo,
    ship: { ...career.ship, hull: opts.shipDef.hull, torpedoes: opts.shipDef.torpedoRounds },
    stats: { ...career.stats, deaths: career.stats.deaths + 1 },
  }
  const fleetShip = flownEntry(career) !== null ? { lost: lost !== null, insured: flownShipInsured(career) } : undefined
  const base: DeathOutcome = fleetShip !== undefined ? { career: rebuilt, contents, story, storyPod, fleetShip } : { career: rebuilt, contents, story, storyPod }
  if (lost === null || opts.limitsOf === undefined) return ok(base, effects)
  const carried = lostCarriedOf(rebuilt, lost)
  const next = fallBackToLeaseShip(rebuilt, data, lost, opts.limitsOf, opts.stationKnown)
  const outcome: DeathOutcome = { ...base, career: next, lostShip: lost }
  if (carried !== null) outcome.lostCarried = carried
  return ok(outcome, effects)
}

/** A copy of the goods carried (the positive quantities), or null when there are none (SY-2b). */
function carriedGoods(parts: Readonly<Goods> | undefined): Goods | null {
  if (parts === undefined) return null
  let out: Goods | null = null
  for (const id of GOOD_IDS) {
    const qty = parts[id] ?? 0
    if (qty > 0) (out ??= {})[id] = qty
  }
  return out
}
